/**
 * Pantalla de **gestión de roles** (`/admin/roles`). Reemplaza el placeholder de
 * `ROUTES.roles`. Equivale a `roles.html` del SSR.
 *
 * Exige `manage_roles` (mismo permiso que la API y el menú del SSR) vía
 * `RequirePermission`. Orquesta:
 *  - las dos secciones (sistema / personalizados) en `RolesTable`;
 *  - alta/edición (`RoleFormDialog`) y borrado de personalizados
 *    (`ConfirmDeleteDialog`).
 *
 * Presentacional + hooks; sin `fetch`. Estados de carga/error contemplados.
 */
import { useState } from "react";

import { Button, Card, CardContent } from "@/components/ui";

import { MANAGE_ROLES_PERMISSION } from "../constants";
import { teamErrorMessage } from "../format";
import { useDeleteRole } from "../hooks/useRoleMutations";
import { useRoles } from "../hooks/useUsers";
import type { Role } from "../types";
import { ConfirmDeleteDialog } from "./ConfirmDeleteDialog";
import { RequirePermission } from "./RequirePermission";
import { RoleFormDialog } from "./RoleFormDialog";
import { RolesTable } from "./RolesTable";

/** Objetivo del formulario de rol (alta o edición). */
type FormTarget = { mode: "create" } | { mode: "edit"; role: Role } | null;

export function RolesScreen() {
  const rolesQuery = useRoles();
  const deleteRole = useDeleteRole();

  const [formTarget, setFormTarget] = useState<FormTarget>(null);
  const [deleteTarget, setDeleteTarget] = useState<Role | null>(null);

  function openDelete(role: Role) {
    deleteRole.reset();
    setDeleteTarget(role);
  }

  function handleConfirmDelete() {
    if (deleteTarget === null) {
      return;
    }
    deleteRole.mutate(deleteTarget.id, {
      onSuccess: () => setDeleteTarget(null),
    });
  }

  return (
    <RequirePermission
      permission={MANAGE_ROLES_PERMISSION}
      title="Roles"
      deniedMessage="No tienes permiso para gestionar roles."
    >
      <div className="flex flex-col gap-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h1 className="text-2xl font-semibold text-slate-900">Roles</h1>
          <Button
            type="button"
            variant="primary"
            onClick={() => setFormTarget({ mode: "create" })}
          >
            Nuevo rol
          </Button>
        </div>

        {rolesQuery.isPending ? (
          <Card>
            <CardContent>
              <p className="py-8 text-center text-sm text-slate-500" role="status">
                Cargando roles…
              </p>
            </CardContent>
          </Card>
        ) : rolesQuery.isError ? (
          <Card>
            <CardContent>
              <p className="py-8 text-center text-sm text-red-600" role="alert">
                {teamErrorMessage(rolesQuery.error)}
              </p>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="flex flex-col gap-8">
              <RolesTable
                title="Roles del sistema"
                roles={rolesQuery.data.system}
                allowDelete={false}
                emptyText="No hay roles del sistema."
                onEdit={(role) => setFormTarget({ mode: "edit", role })}
              />
              <RolesTable
                title="Roles personalizados"
                roles={rolesQuery.data.custom}
                allowDelete
                emptyText="No hay roles personalizados."
                onEdit={(role) => setFormTarget({ mode: "edit", role })}
                onDelete={openDelete}
              />
            </CardContent>
          </Card>
        )}
      </div>

      <RoleFormDialog target={formTarget} onClose={() => setFormTarget(null)} />
      <ConfirmDeleteDialog
        open={deleteTarget !== null}
        title="Eliminar rol"
        description={
          deleteTarget
            ? `¿Eliminar el rol "${deleteTarget.name}"? Esta acción no se puede deshacer.`
            : ""
        }
        errorMessage={deleteRole.isError ? teamErrorMessage(deleteRole.error) : null}
        isPending={deleteRole.isPending}
        onConfirm={handleConfirmDelete}
        onClose={() => setDeleteTarget(null)}
      />
    </RequirePermission>
  );
}
